import React, { createContext, useContext, useState } from 'react'
import AvatarCelebration from '../components/avatar/AvatarCelebration'
import useCelebration from '../hooks/useCelebration'

const CelebrationContext = createContext({})

export const useCelebrationContext = () => {
  const context = useContext(CelebrationContext)
  if (!context) {
    throw new Error('useCelebrationContext must be used within a CelebrationProvider')
  }
  return context
}

export const CelebrationProvider = ({ children }) => {
  const {
    celebrationTrigger,
    celebrationType,
    celebrationCharacter,
    celebrationMessage,
    celebrationPoints, // New state for points
    celebrateTaskComplete,
    celebrateLevelUp,
    celebrateRewardEarned,
    celebrateStreak,
    triggerCelebration
  } = useCelebration()
  
  const [celebrationQueue, setCelebrationQueue] = useState([])
  const [isPlaying, setIsPlaying] = useState(false)

  const queueCelebration = (celebration) => {
    setCelebrationQueue(prev => [...prev, celebration])
  }

  const playNextCelebration = () => {
    if (celebrationQueue.length > 0 && !isPlaying) {
      const next = celebrationQueue[0]
      setCelebrationQueue(prev => prev.slice(1))
      setIsPlaying(true)
      
      // Trigger the celebration
      triggerCelebration(next)
    }
  }

  const onCelebrationComplete = () => {
    setIsPlaying(false)
    // Play next in queue after a short delay
    setTimeout(() => {
      playNextCelebration()
    }, 500)
  }

  // Auto-play celebrations from queue
  React.useEffect(() => {
    playNextCelebration()
  }, [celebrationQueue.length])

  const value = {
    celebrateTaskComplete: (childAvatar, taskTitle, pointsEarned) => {
      const celebration = {
        type: 'task_complete',
        character: childAvatar,
        message: taskTitle ? `Great job on "${taskTitle}"! 🌟` : '',
        pointsEarned: pointsEarned // Include actual points
      }
      queueCelebration(celebration)
    },
    
    celebrateLevelUp: (childAvatar, newLevel) => {
      const celebration = {
        type: 'level_up',
        character: childAvatar,
        message: `Level ${newLevel} achieved! 🚀`
      }
      queueCelebration(celebration)
    },
    
    celebrateRewardEarned: (childAvatar, rewardTitle) => {
      const celebration = {
        type: 'reward_earned',
        character: childAvatar,
        message: rewardTitle ? `You earned "${rewardTitle}"! 🎁` : ''
      }
      queueCelebration(celebration)
    },
    
    celebrateStreak: (childAvatar, streakDays) => {
      const celebration = {
        type: 'streak',
        character: childAvatar,
        message: `${streakDays} day streak! 🔥`
      }
      queueCelebration(celebration)
    },
    
    triggerCelebration: (celebration) => {
      queueCelebration(celebration)
    }
  }

  return (
    <CelebrationContext.Provider value={value}>
      {children}
      
      {/* Celebration Component */}
      <AvatarCelebration
        trigger={celebrationTrigger}
        character={celebrationCharacter}
        celebrationType={celebrationType}
        customMessage={celebrationMessage}
        pointsEarned={celebrationPoints} // Pass the actual points
        onComplete={onCelebrationComplete}
      />
    </CelebrationContext.Provider>
  )
}